import { useState, useEffect, useRef, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "./../../utils/axiosInstance";
import { ungroupNode, updateNode } from "../../utils/flowSlice";
import { Handle, Position } from "@xyflow/react";
import { FaInstagram } from "react-icons/fa";
import { FiExternalLink } from "react-icons/fi";
import { BiLoaderCircle } from "react-icons/bi";
import { FaRegObjectUngroup } from "react-icons/fa6";
import { DarkModeContext } from "../../context/DarkModeContext";
import toast from "react-hot-toast";

const InstagramNode = ({ data, isConnectable }) => {
  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState(data.title || "Fetching the title");
  const [note, setNote] = useState(data.note || "");
  const script = data.script || "";
  const imageUrl = data.imageUrl;
  const dispatch = useDispatch();
  const nodes = useSelector((store) => store.flow.nodes);
  const currentNode = nodes.find((node) => node.id === data.id);
  const isGrouped = !!currentNode ? !!currentNode.parentId : false;
  const unGroup = () =>
    dispatch(
      ungroupNode({
        id: data.id,
      })
    );

  // console.log(data.sourceUrl);

  const fetchScript = async (url) => {
    try {
      if (title !== "Fetching the title" && script !== "") return;
      setLoading(true);
      const response1 = await axios.post(
        `${process.env.REACT_APP_BASED_URL}/service/instagram/script`,
        {
          url,
        }
      );
      const tempScript = response1.data;

      const response2 = await axios.post(
        `${process.env.REACT_APP_BASED_URL}/service/title`,
        {
          tempScript,
        }
      );
      const temptitle = response2.data;

      setLoading(false);
      setTitle(temptitle);
      dispatch(
        updateNode({
          id: data.id,
          data: {
            ...data,
            script: tempScript,
            title: temptitle,
          },
        })
      );
    } catch (err) {
      console.log(err);
      if (err.status === 402) {
        toast.error(
          "You have run out of your credit. You need to upgrade your plan"
        );
      }
    }
  };

  useEffect(() => {
    fetchScript(data.sourceUrl);
  }, [data.sourceUrl]);

  const { darkMode } = useContext(DarkModeContext);
  const textAreaRef = useRef(null);
  const handleInputChange = (e) => {
    setNote(e.target.value);
    if (e.target.scrollHeight > 50) {
      e.target.style.height = `${e.target.scrollHeight}px`;
    }
    if (e.target.value === "") {
      e.target.style.height = `${50}px`;
    }
    dispatch(
      updateNode({
        id: data.id,
        data: {
          ...data,
          note: e.target.value,
        },
      })
    );
  };

  return (
    <div>
      <Handle
        type="source"
        position={Position.Right}
        isConnectable={isConnectable}
        style={{
          width: "20px", // Default width
          height: "20px", // Default height
          background: "white", // Default background color
          border: "2px solid #32b5e5", // Default border color
          borderRadius: "50%", // Circular shape
          position: "absolute",
          right: "-20px", // Ensure proper alignment
          // top: "50%", // Align vertically
          transform: "translateY(-50%)", // Center the handle
          display: "flex",
          alignItems: "center",
          lineHeight: "20px", // Match the handle's height for vertical centering
          fontSize: "12px", // Ensure text size fits
          justifyContent: "center",
          transition: "transform 0.2s, background-color 0.2s, right 0.2s", // Smooth hover transition
        }}
        onMouseEnter={(e) => {
          e.target.style.transform = "translateY(-50%) scale(1.5)"; // Keep alignment and scale
          e.target.style.background = "#32b5e5"; // Change background to blue
          e.target.style.color = "white"; // Set text color to white
          e.target.innerHTML = "+"; // Add "+" symbol inside
        }}
        onMouseLeave={(e) => {
          e.target.style.transform = "translateY(-50%) scale(1)"; // Reset scale while maintaining alignment
          e.target.style.background = "white"; // Reset background to white
          e.target.style.color = ""; // Clear the text color
          e.target.innerHTML = ""; // Remove "+" symbol
        }}
      />

      <div
        className={`flex flex-col gap-1 w-[375px] mx-auto rounded-[10px] ${
          darkMode ? "bg-[#eeeee6]" : "bg-[#080808]"
        }`}
        tabIndex="0"
      >
        <div className="flex justify-between items-center bg-[#d56cf0] text-white px-4 py-2 rounded-[9px]">
          <div className="flex items-center space-x-2">
            {isGrouped && (
              <FaRegObjectUngroup
                size={"16"}
                className="hover:cursor-pointer"
                onClick={() => unGroup()}
              />
            )}
            {loading ? (
              <div className="flex items-center justify-start">
                <BiLoaderCircle
                  size={"18"}
                  className="loading-icon"
                  color="white"
                />
                <span className="ml-2 flex justify-start font-semibold text-[16px]">
                  Fetching the title
                </span>
              </div>
            ) : (
              <div className="flex items-center justify-start">
                <FaInstagram size={"18"} />
                <span className="ml-2 font-semibold text-xs text-left overflow-hidden text-nowrap overflow-ellipsis w-[250px]">
                  {title}
                </span>
              </div>
            )}
          </div>
          <FiExternalLink
            size={"16"}
            className="hover:cursor-pointer"
            onClick={() => {
              window.open(data.sourceUrl || null, "_blank");
            }}
          />
        </div>

        {imageUrl ? (
          <div className="relative">
            <img
              src={imageUrl}
              alt="instagram thumbnail"
              className="w-full h-[553px] rounded-[8px]"
            />
          </div>
        ) : (
          <div className="w-full h-[553px] rounded-[8px] bg-white"></div>
        )}

        {/* Notes Section */}
        <textarea
          ref={textAreaRef}
          className={` nodrag w-full h-[36px] min-h-[50px] max-h-[120px] ${
            darkMode
              ? "bg-[#f5f8fa] border-neutral-300 text-black"
              : "bg-[#292929] border-[#292929] text-neutral-200"
          } text-sm font-sans  p-1.5 pr-3 rounded-[8px]  focus-within:outline-none resize-y `}
          placeholder={"Add notes for AI to use..."}
          value={note}
          onChange={(e) => handleInputChange(e)}
          rows={1}
        ></textarea>
      </div>
    </div>
  );
};

export default InstagramNode;
